import React, { useCallback } from 'react'

import { TextField } from '@tribegroup'
import { ITextFieldProps } from '@tribegroup/TextField/TextField'
import { normalizeUrl } from '@utils/urls'

/**
 * UrlInput - A drop-in replacement for TextField with automatic HTTPS normalization on paste
 *
 * Features:
 * - Auto-prepends https:// when URL is pasted without protocol
 * - Upgrades http:// to https:// on paste
 * - 100% compatible with TextField API - just replace TextField with UrlInput
 *
 * @example
 * ```tsx
 * // Before:
 * <TextField name="deal_link" value={dealLink} onChange={handleChange} ... />
 *
 * // After (drop-in replacement):
 * <UrlInput name="deal_link" value={dealLink} onChange={handleChange} ... />
 * ```
 */
export const UrlInput: React.FC<ITextFieldProps> = ({ onChange, onPaste, ...props }) => {
  /**
   * Handle paste events - normalize URL to https://
   */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      // Call original onPaste if provided
      onPaste?.(event)

      // If original handler didn't prevent default, normalize the URL
      if (!event.defaultPrevented) {
        event.preventDefault()
        const pastedText = event.clipboardData.getData('text')
        const normalizedUrl = normalizeUrl(pastedText)

        // Set the input value directly
        const input = event.currentTarget
        input.value = normalizedUrl

        // Create and dispatch a new input event
        const inputEvent = new Event('input', { bubbles: true })
        input.dispatchEvent(inputEvent)
        // Call onChange with synthetic event
        if (onChange) {
          const syntheticEvent = {
            target: input,
            currentTarget: input,
          } as React.ChangeEvent<HTMLInputElement>
          onChange(syntheticEvent)
        }
      }
    },
    [onChange, onPaste],
  )

  return <TextField {...props} onChange={onChange} onPaste={handlePaste} />
}

export default UrlInput
